import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { useAppSelector } from "./redux-hooks";
import {
    fetchAllJobs,
    scheduleJob,
    runJobNow,
    editJobDetails,
    changeJobStatus,
    changeJobScheduleStatus,
    JobFilters,
    JobRequest,
    EditJobRequest,
    ChangeJobStatusRequest
} from "../../service/JobService";


// Schedule Type Options
export const SCHEDULE_TYPE_OPTIONS = [
    { value: 'minutely', label: 'MINUTELY' },
    { value: 'hourly', label: 'HOURLY' },
    { value: 'daily', label: 'DAILY' },
    { value: 'weekly', label: 'WEEKLY' },
    { value: 'monthly', label: 'MONTHLY' }
    // { value: 'cron', label: 'CRON' }
];

// Common Filter Options
export const ENABLED_OPTIONS = [
    { value: 'YES', label: 'YES' },
    { value: 'NO', label: 'NO' }
];

export const SCHEDULED_OPTIONS = [
    { value: 'YES', label: 'YES' },
    { value: 'NO', label: 'NO' }
];

export interface Job {
    jobId: number;
    jobName: string;
    jobParams?: Record<string, string>;
    className: string;
    scheduleType: string;
    scheduleTime: string;
    dayOfWeek?: string;
    dayOfMonth?: string;
    lastRunTime: string;
    scheduled: boolean;
    inActiveDate?: string;
    auditUserId: string;
    type: string;
    lastSyncDateTime: string;
    lastSyncStatus: string;
    serviceClass?: string;
    entityClass?: string;
    auditTimestamp: string;
    enabled?: boolean;
    description?: string;//CS-1571
}

interface JobState {
    allJobs: Job[];
    jobs: Job[];
    currentFilters: JobFilters;
    error: any;
}

const initialState: JobState = {
    allJobs: [],
    jobs: [],
    currentFilters: {
        jobName: [],
        enabled: [],
        scheduled: [],
        scheduleType: [],
    },
    error: null,
};

export const fetchJobsList = createAsyncThunk(
    "jobs/fetchJobsList",
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchAllJobs();
            const v3Response = response.data;
            return v3Response.data;
        } catch (error: any) {
            return rejectWithValue(error?.data);
        }
    }
);

export const scheduleJobAsync = createAsyncThunk(
    "jobs/scheduleJob",
    async (params: { jobName: string; scheduleData?: Partial<JobRequest> }, { rejectWithValue }) => {
        try {
            const response = await scheduleJob(params.jobName, params.scheduleData);
            return { jobName: params.jobName, response: response.data };
        } catch (error: any) {
            return rejectWithValue(error?.data);
        }
    }
);

// export const unscheduleJobAsync = createAsyncThunk(
//     "jobs/unscheduleJob",
//     async (jobName: string, { rejectWithValue }) => {
//         try {
//             const response = await unscheduleJob(jobName);
//             return { jobName, response: response.data };
//         } catch (error: any) {
//             return rejectWithValue(error?.data);
//         }
//     }
// );

export const runJobAsync = createAsyncThunk(
    "jobs/runJob",
    async (jobName: string, { rejectWithValue }) => {
        try {
            const response = await runJobNow(jobName);
            return { jobName, response: response.data };
        } catch (error: any) {
            return rejectWithValue(error?.data);
        }
    }
);

export const editJobAsync = createAsyncThunk(
    "jobs/editJob",
    async (editJobRequest: EditJobRequest, { rejectWithValue }) => {
        try {
            const response = await editJobDetails(editJobRequest);
            return { jobId: editJobRequest.jobId, response: response.data };
        } catch (error: any) {
            return rejectWithValue(error?.data);
        }
    }
);

export const changeJobStatusAsync = createAsyncThunk(
    "jobs/changeJobStatus",
    async (changeJobStatusRequest: ChangeJobStatusRequest, { rejectWithValue }) => {
        try {
            const response = await changeJobStatus(changeJobStatusRequest);
            return { jobId: changeJobStatusRequest.jobId, response: response.data };
        } catch (error: any) {
            return rejectWithValue(error?.data);
        }
    }
);

export const changeJobScheduleStatusAsync = createAsyncThunk(
    "jobs/changeJobScheduleStatus",
    async (changeJobStatusRequest: ChangeJobStatusRequest, { rejectWithValue }) => {
        try {
            const response = await changeJobScheduleStatus(changeJobStatusRequest);
            return { jobId: changeJobStatusRequest.jobId, response: response.data };
        } catch (error: any) {
            return rejectWithValue(error?.data);
        }
    }
);

const computeEnabledStatus = (job: Job): Job => {
    let enabled = true;

    if (job.inActiveDate) {
        const inActiveDate = new Date(job.inActiveDate);
        const currentDate = new Date();
        enabled = inActiveDate > currentDate;
        console.log('Computing enabled status:', {
            jobName: job.jobName,
            inActiveDate: job.inActiveDate,
            parsedDate: inActiveDate,
            currentDate,
            enabled
        });
    }

    return { ...job, enabled };
};

const processJobs = (jobs: Job[]): Job[] => {
    return jobs.map(computeEnabledStatus);
};

const filterJobs = (jobs: Job[], filters: JobFilters): Job[] => {
    // console.log('Filtering jobs:', { totalJobs: jobs.length, filters });

    const filteredJobs = jobs.filter(job => {
        if (filters.jobName.length && !filters.jobName.includes(job.jobName)) {
            return false;
        }

        if (filters.enabled.length) {
            const enabledStatus = job.enabled ? 'YES' : 'NO';
            // console.log('Enabled filter check:', { 
            //     jobName: job.jobName, 
            //     jobEnabled: job.enabled, 
            //     enabledStatus, 
            //     filterValues: filters.enabled,
            //     includes: filters.enabled.includes(enabledStatus)
            // });
            if (!filters.enabled.includes(enabledStatus)) {
                return false;
            }
        }

        if (filters.scheduled.length) {
            const scheduledStatus = job.scheduled ? 'YES' : 'NO';
            // console.log('Scheduled filter check:', { 
            //     jobName: job.jobName, 
            //     jobScheduled: job.scheduled, 
            //     scheduledStatus, 
            //     filterValues: filters.scheduled,
            //     includes: filters.scheduled.includes(scheduledStatus)
            // });
            if (!filters.scheduled.includes(scheduledStatus)) {
                return false;
            }
        }

        if (filters.scheduleType.length && !filters.scheduleType.includes(job.scheduleType)) {
            return false;
        }

        return true;
    });

    return filteredJobs;
};

const jobSlice = createSlice({
    name: "jobSlice",
    initialState,
    reducers: {
        clearJobError: (state) => {
            state.error = null;
        },


        updateJobStatus: (state, action: PayloadAction<{ jobName: string; enabled?: boolean; scheduled?: boolean }>) => {
            const { jobName, enabled, scheduled } = action.payload;

            const allJobIndex = state.allJobs.findIndex(job => job.jobName === jobName);
            if (allJobIndex !== -1) {
                if (enabled !== undefined) {
                    state.allJobs[allJobIndex].enabled = enabled;
                }
                if (scheduled !== undefined) {
                    state.allJobs[allJobIndex].scheduled = scheduled;
                }
            }

            const jobIndex = state.jobs.findIndex(job => job.jobName === jobName);
            if (jobIndex !== -1) {
                if (enabled !== undefined) {
                    state.jobs[jobIndex].enabled = enabled;
                }
                if (scheduled !== undefined) {
                    state.jobs[jobIndex].scheduled = scheduled;
                }
            }
        },
        applyFilters: (state, action: PayloadAction<JobFilters>) => {
            state.currentFilters = action.payload;
            state.jobs = filterJobs(state.allJobs, action.payload);
        },
        clearFilters: (state) => {
            state.currentFilters = {
                jobName: [],
                enabled: [],
                scheduled: [],
                scheduleType: [],
            };
            state.jobs = [...state.allJobs];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchJobsList.fulfilled, (state, action) => {
                // const processedJobs = processJobs(action.payload);
                const processedJobs = processJobs(action.payload ?? []); // CS-1984
                state.allJobs = processedJobs;
                state.jobs = filterJobs(processedJobs, state.currentFilters);
            })
            .addCase(scheduleJobAsync.fulfilled, (state, action) => {
                const allJobIndex = state.allJobs.findIndex(job => job.jobName === action.payload.jobName);
                if (allJobIndex !== -1) {
                    state.allJobs[allJobIndex].scheduled = true;
                }

                const jobIndex = state.jobs.findIndex(job => job.jobName === action.payload.jobName);
                if (jobIndex !== -1) {
                    state.jobs[jobIndex].scheduled = true;
                }
            })
            // .addCase(unscheduleJobAsync.pending, (state) => {
            //     state.unscheduleStatus = "loading";
            //     state.error = null;
            // })
            // .addCase(unscheduleJobAsync.fulfilled, (state, action) => {
            //     state.unscheduleStatus = "success";

            //     const allJobIndex = state.allJobs.findIndex(job => job.jobName === action.payload.jobName);
            //     if (allJobIndex !== -1) {
            //         state.allJobs[allJobIndex].scheduled = false;
            //     }

            //     const jobIndex = state.jobs.findIndex(job => job.jobName === action.payload.jobName);
            //     if (jobIndex !== -1) {
            //         state.jobs[jobIndex].scheduled = false;
            //     }
            // })
            // .addCase(unscheduleJobAsync.rejected, (state, action) => {
            //     state.unscheduleStatus = "error";
            //     state.error = action.payload || "Failed to unschedule job";
            // })
            // Run Job
            .addCase(runJobAsync.fulfilled, (state, action) => {
                const currentTime = new Date().toLocaleString();

                const allJobIndex = state.allJobs.findIndex(job => job.jobName === action.payload.jobName);
                if (allJobIndex !== -1) {
                    state.allJobs[allJobIndex].lastRunTime = currentTime;
                }

                const jobIndex = state.jobs.findIndex(job => job.jobName === action.payload.jobName);
                if (jobIndex !== -1) {
                    state.jobs[jobIndex].lastRunTime = currentTime;
                }
            })
            .addCase(editJobAsync.fulfilled, () => {
                // Job edit success - no state changes needed as we refresh the list
            })
            .addCase(changeJobStatusAsync.fulfilled, (state, action) => {
                const jobId = action.payload.jobId;

                const allJobIndex = state.allJobs.findIndex(job => job.jobId.toString() === jobId);
                if (allJobIndex !== -1) {
                    state.allJobs[allJobIndex].enabled = !state.allJobs[allJobIndex].enabled;
                }

                const jobIndex = state.jobs.findIndex(job => job.jobId.toString() === jobId);
                if (jobIndex !== -1) {
                    state.jobs[jobIndex].enabled = !state.jobs[jobIndex].enabled;
                }
            })
            .addCase(changeJobScheduleStatusAsync.fulfilled, (state, action) => {
                const jobId = action.payload.jobId;

                const allJobIndex = state.allJobs.findIndex(job => job.jobId.toString() === jobId);
                if (allJobIndex !== -1) {
                    state.allJobs[allJobIndex].scheduled = !state.allJobs[allJobIndex].scheduled;
                }

                const jobIndex = state.jobs.findIndex(job => job.jobId.toString() === jobId);
                if (jobIndex !== -1) {
                    state.jobs[jobIndex].scheduled = !state.jobs[jobIndex].scheduled;
                }
            });
    },
});

export const { clearJobError, updateJobStatus, applyFilters, clearFilters } = jobSlice.actions;
export const useJobSlice = () => useAppSelector((state) => state.jobSlice);
export default jobSlice.reducer;